import React from 'react';
import { DynamicIcon } from 'lucide-react/dynamic';
import Input from '@ajat/design-system/Input';
import maxLengthFromRegex, { sanitizeForRegex, inputModeFromRegex } from './attributeTextLimits';
import { PRODUCT_FOCUS_REVEAL } from './focusReveal';
import { HeadingSmall, LabelSelector, LargeLabelSelector } from '@ajat/design-system/Configurator';

/**
 * DynamicAttributeControls
 *
 * Renders the BE-driven product attributes that are NOT the canonical color
 * (type === 'color') or size (slug === 'size') selectors. These live inside a
 * brand add-ons section (e.g. CLS-DK "Tilføjelser") and are designed to match
 * the brand's static add-on fields:
 *
 *   - textfield / dropdown  → "Navnetryk" layout  (heading + "?" + price,
 *                              input, and a helper line carrying the subtitle)
 *   - select / text         → "Lille Brysttryk" layout (heading + "?" +
 *                              subtitle as extra info, then a toggle row)
 *
 * Every control surfaces the attribute's Hint (hover tooltip on the "?") and,
 * when present, its Hint HTML (popup opened via `onInfoOpen`). Select options
 * may render a Lucide icon instead of their label.
 *
 * The selector behaviour itself (value selection, price contribution) is
 * unchanged — only the surrounding chrome is aligned with the static fields.
 */

/** Format an additional_price as a "+NN,-" tag, or null when there's no charge. */
const formatAdditionalPrice = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `+${Math.round(n)},-`;
};

const disabledStyle = { opacity: 0.35, cursor: 'not-allowed' };

const DynamicAttributeControls = ({
  attributes = [],
  selectedAttributes = {},
  onAttributeChange,
  /** Called with the attribute slug when a textfield gains focus (3D pages
   *  move the camera to the print the customer is about to type). */
  onAttributeFocus,
  /** Mobile focus mode: when given together with `isMobile`, a textfield
   *  renders as a `mf__trigger` (id `mf-trigger-{slug}`) whose tap
   *  calls `openFocusMode(slug)` — the page then edits the value in its
   *  MobileFocusModal (same pattern as the static Navnetryk in
   *  ConfiguratorSections). Without it the input is editable inline. */
  openFocusMode,
  isMobile = false,
  onAttributeBlur,
  /** 3D pages on mobile: textfields pin themselves above the keyboard (DS Input
   *  mobile focus, bottom placement) and report it via onTypingChange(active, slug). */
  typingMode = false,
  onTypingChange,
  onInfoOpen,
  disabledValueIdsByAttribute = {},
  hiddenControlSlugs = new Set(),
  /** { [slug]: label } — linked (constraint) selects/dropdowns that cannot hold their
   *  group's choice (e.g. a print-only flag after switching to embroidery): the control
   *  stays EMPTY and is never preselected with a default, until the customer picks one
   *  of its real options (which moves the group). See constraints/attributeConstraints.js. */
  unavailableLinked = {},
  /** Inline error under a textfield whose value fails the attribute's regex
   *  (the BE rejects such input at add-to-cart with a 422). Danish default;
   *  SE/FI pages pass their own copy. */
  textfieldErrorText = 'Teksten er for lang eller indeholder ugyldige tegn',
  /** When true, required textfields that are still empty show
   *  `requiredErrorText` inline — the PDP flips this on when add-to-cart is
   *  attempted with a missing required field (mirrors the BE's 422). */
  showRequiredErrors = false,
  requiredErrorText = 'Udfyld venligst dette felt',
  /** Slugs of revealed-by-rule select/dropdown controls that must be answered
   *  (see findUnansweredRevealedSlugs) — they error like required fields when
   *  `showRequiredErrors` flips on. */
  requiredEmptySlugs = new Set(),
  /** Labels over the class-logo preview images when both sides exist. */
  logoSideLabels = { front: 'Forside', back: 'Bagside' },
}) => {
  const hiddenSet = hiddenControlSlugs instanceof Set ? hiddenControlSlugs : new Set(hiddenControlSlugs || []);
  const requiredEmptySet = requiredEmptySlugs instanceof Set ? requiredEmptySlugs : new Set(requiredEmptySlugs || []);
  const items = attributes.filter(
    (a) => a.slug !== 'color' && a.slug !== 'size' && a.type !== 'color' && !hiddenSet.has(a.slug),
  );

  // Required "logo" attributes are forced on: default them to their anchor
  // value (the "on" state) — the cross/off option is hidden in the UI.
  // Required selects with exactly ONE enabled option (e.g. after class-level
  // overrides disabled the others) are auto-selected the same way, so their
  // price, visibility-rule dependencies and saved options all engage without
  // requiring a click on a choice that isn't really a choice.
  React.useEffect(() => {
    attributes.forEach((attr) => {
      if (hiddenSet.has(attr.slug)) return;

      if (attr.type === 'logo') {
        const anchorId = attr.values?.[0]?.id;
        // No resolved image (school logo not uploaded / class logo not
        // approved) → the control is inactive: never force it on, and clear
        // a stale selection (e.g. restored from an old order) so its add-on
        // price is never charged for artwork that doesn't exist.
        if (!attr.logo_image_url) {
          if (selectedAttributes[attr.slug] != null) {
            onAttributeChange?.(attr.slug, null);
          }
          return;
        }
        // Display-only logos are purely cosmetic: never selected/priced/saved,
        // so clear any stale selection and never force them on.
        if (attr.display_only) {
          if (selectedAttributes[attr.slug] != null) {
            onAttributeChange?.(attr.slug, null);
          }
          return;
        }
        if (attr.is_required && anchorId != null && selectedAttributes[attr.slug] !== anchorId) {
          onAttributeChange?.(attr.slug, anchorId);
        }
        return;
      }

      if (attr.type !== 'select' && attr.type !== 'dropdown') return;
      // Linked field whose group's choice it cannot hold: stays empty — a default here
      // would silently change the linked field the customer did choose.
      if (unavailableLinked[attr.slug]) return;
      const pinnedIds   = attr.pinnedValueIds ?? [];
      const disabledIds = disabledValueIdsByAttribute[attr.slug] ?? null;
      const enabled = (attr.values || []).filter((v) =>
        !pinnedIds.includes(v.id)
        && !(disabledIds instanceof Set && disabledIds.has(v.id)));
      if (!enabled.length) return;
      const current = selectedAttributes[attr.slug];
      if (attr.is_required) {
        // Required select/dropdown → always preselect: the admin-flagged
        // default value, else the top (first) option. Keeps a valid selection.
        if (current != null && enabled.some((v) => v.id === current)) return;
        const preferred = enabled.find((v) => v.is_default) ?? enabled[0];
        onAttributeChange?.(attr.slug, preferred.id);
        return;
      }
      // Optional → preselect an explicitly flagged default, or the ONLY
      // enabled option (a one-choice control defaults to its choice), and
      // only while untouched (undefined). A user clear stores null, which
      // sticks — the default must never re-force itself.
      if (current !== undefined) return;
      const def = enabled.find((v) => v.is_default) ?? (enabled.length === 1 ? enabled[0] : null);
      if (def) onAttributeChange?.(attr.slug, def.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, selectedAttributes, disabledValueIdsByAttribute, unavailableLinked]);

  if (items.length === 0) return null;

  // User-initiated changes also report the attribute as "in focus" (3D pages
  // move the camera); the auto-selection effects above deliberately don't.
  const userChange = (slug, value) => { onAttributeFocus?.(slug); onAttributeChange?.(slug, value); };
  return (
    <>
      {items.map((attr) => {
        const selectedId = selectedAttributes[attr.slug];
        const pinnedIds  = attr.pinnedValueIds ?? [];
        const disabledIds = disabledValueIdsByAttribute[attr.slug] ?? null;

        // Hint → hover tooltip on the "?"; Hint HTML → click-to-open popup.
        const hintHtml = (attr.hint_html || '').trim();
        const infoTooltip = attr.hint || undefined;
        const onInfoClick = hintHtml
          ? () => onInfoOpen?.(hintHtml, attr.name)
          : undefined;

        /* ── logo → On/Off toggle (checkmark / cross) with a preview ── */
        if (attr.type === 'logo') {
          const anchorId = attr.values?.[0]?.id;
          const isOn = anchorId != null && selectedId === anchorId;
          const required = !!attr.is_required;
          // Heading = the attribute's per-language name (the admin's
          // "Attribute Name" translations field).
          const logoTitle = attr.name;
          const logoUrl = attr.logo_image_url;
          // No resolved image (school logo not uploaded / class logo not
          // approved / no school context) → hide the control entirely. The
          // effect above already cleared any stale selection, so the add-on
          // is never priced or saved without real artwork.
          if (!logoUrl) return null;
          // Display-only mode: just the heading + the resolved logo image
          // (click to zoom). No toggle, no price — position via attribute
          // sort_order (e.g. right above Rygtryk).
          if (attr.display_only) {
            // Class logos can carry a front AND a back drawing (create-school
            // Forsiden/Bagsiden). Both sides render labeled when both exist;
            // one side → one labeled image; none → the block is hidden (the
            // !logoUrl guard above, logoUrl = front ?? back).
            const sides = [
              { url: attr.logo_image_front_url || (attr.logo_image_back_url ? null : logoUrl), label: logoSideLabels.front },
              { url: attr.logo_image_back_url, label: logoSideLabels.back },
            ].filter((s) => !!s.url);
            const showLabels = sides.length > 1
              || (!!attr.logo_image_back_url && !attr.logo_image_front_url)
              || (!!attr.logo_image_front_url && attr.logo_source === 'class');
            const openDisplayPreview = (url) => () => onInfoOpen?.(
              /\.pdf(\?|#|$)/i.test(url)
                ? `<iframe src="${url}" title="${logoTitle}" style="width:100%;height:70vh;border:0"></iframe>`
                : `<div style="text-align:center"><img src="${url}" alt="" style="max-width:100%;height:auto" /></div>`,
              logoTitle,
            );
            return (
              <div key={attr.slug} className="pp__section pp__section--logo-display" data-cs-section={attr.slug} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {sides.map((side) => (
                  <div key={side.label} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {showLabels && (
                      <span className="typo-body-sm" style={{ color: 'var(--color-gray-7)' }}>{side.label}</span>
                    )}
                    {/\.pdf(\?|#|$)/i.test(side.url) ? (
                      <button
                        type="button"
                        onClick={openDisplayPreview(side.url)}
                        className="typo-body-sm"
                        style={{ height: '64px', padding: '0 12px', cursor: 'zoom-in', border: '1px solid #eee', borderRadius: '6px', background: '#fff', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <DynamicIcon name="file-text" size={18} /> PDF
                      </button>
                    ) : (
                      <img
                        src={side.url}
                        alt={`${logoTitle} — ${side.label}`}
                        onClick={openDisplayPreview(side.url)}
                        role="button"
                        style={{ height: '144px', maxWidth: '320px', objectFit: 'contain', cursor: 'zoom-in', border: '1px solid #eee', borderRadius: '6px', padding: '4px', background: '#fff' }}
                      />
                    )}
                  </div>
                ))}
              </div>
            );
          }
          const logoInactive = false;
          // The logo image (esp. a class logo) can be a PDF, which can't render
          // in an <img> — show it in an inline viewer instead.
          const logoIsPdf = /\.pdf(\?|#|$)/i.test(logoUrl || '');
          // The logo add-on price lives on the anchor value (like textfield);
          // show its "+NN,-" tag on the control.
          const logoPriceTag = logoInactive
            ? ''
            : formatAdditionalPrice(attr.values?.[0]?.additional_price);
          const openPreview = logoUrl
            ? () => onInfoOpen?.(
                logoIsPdf
                  ? `<iframe src="${logoUrl}" title="${logoTitle}" style="width:100%;height:70vh;border:0"></iframe>`
                  : `<div style="text-align:center"><img src="${logoUrl}" alt="" style="max-width:100%;height:auto" /></div>`,
                logoTitle,
              )
            : undefined;
          return (
            <div key={attr.slug} className="pp__section" data-cs-section={attr.slug}>
              <HeadingSmall
                title={logoTitle}
                extraInfo={attr.subtitle || undefined}
                infoTooltip={infoTooltip}
                onInfoClick={onInfoClick}
                price={logoPriceTag || undefined}
              />
              <div className="pp__toggle-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <LargeLabelSelector
                  selected={isOn}
                  disabled={logoInactive}
                  onClick={logoInactive ? undefined : () => userChange(attr.slug, anchorId)}
                >
                  <DynamicIcon name="check" size={20} />
                </LargeLabelSelector>
                {(!required || logoInactive) && (
                  <LargeLabelSelector
                    selected={!isOn}
                    disabled={logoInactive}
                    onClick={logoInactive ? undefined : () => userChange(attr.slug, null)}
                  >
                    <DynamicIcon name="x" size={20} />
                  </LargeLabelSelector>
                )}
                {logoUrl && (logoIsPdf ? (
                  <button
                    type="button"
                    onClick={openPreview}
                    className="typo-body-sm"
                    style={{ height: '44px', padding: '0 12px', cursor: 'zoom-in', border: '1px solid #eee', borderRadius: '6px', background: '#fff', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    <DynamicIcon name="file-text" size={18} /> PDF
                  </button>
                ) : (
                  <img
                    src={logoUrl}
                    alt={logoTitle}
                    onClick={openPreview}
                    role="button"
                    style={{ height: '44px', maxWidth: '90px', objectFit: 'contain', cursor: 'zoom-in', border: '1px solid #eee', borderRadius: '6px', padding: '2px', background: '#fff' }}
                  />
                ))}
              </div>
            </div>
          );
        }

        /* ── textfield / dropdown → "Navnetryk" layout ── */
        if (attr.type === 'textfield') {
          const anchor = attr.values?.[0];
          const priceTag = formatAdditionalPrice(anchor?.additional_price);
          const currentValue = typeof selectedId === 'string' ? selectedId : '';
          const isRequired = !!attr.is_required;
          // Live mirror of the BE's add-to-cart check (preg_match on the
          // trimmed input) so the customer sees WHY the cart rejects the text
          // instead of a silent console error.
          let patternError = '';
          const trimmed = currentValue.trim();
          if (trimmed && attr.regex) {
            try {
              if (!(new RegExp(attr.regex, 'u')).test(trimmed)) {
                patternError = textfieldErrorText;
              }
            } catch { /* regex not valid in JS — the server stays the judge */ }
          }
          // Empty required field: silent until the customer tries to add to
          // cart, then inline until filled (matches the BE refusal).
          const requiredError = (isRequired && showRequiredErrors && !trimmed) ? requiredErrorText : '';
          const useFocusTrigger = isMobile && typeof openFocusMode === 'function';
          /* The focus modal owns mobile editing when it is in play; where it is not
           * (3D pages), the field pins itself above the keyboard instead. */
          const input = (
            <Input
              type="text"
              showIcon={false}
              label={attr.name}
              placeholder={attr.name}
              value={currentValue}
              pattern={attr.regex || undefined}
              maxLength={maxLengthFromRegex(attr.regex) || undefined}
              inputMode={inputModeFromRegex(attr.regex) || undefined}
              helperText={attr.subtitle || ''}
              error={patternError || requiredError}
              // Characters the attribute's class forbids never enter the field (Nummertryk: digits only).
              onChange={(e) => userChange(attr.slug, sanitizeForRegex(e.target.value, attr.regex))}
              onFocus={() => onAttributeFocus?.(attr.slug)}
              readOnly={useFocusTrigger}
              tabIndex={useFocusTrigger ? -1 : undefined}
              onBlur={() => onAttributeBlur?.(attr.slug)}
              mobileFocus={!useFocusTrigger && !!typingMode}
              mobileFocusPlacement="bottom"
              onMobileFocusChange={!useFocusTrigger && typingMode
                ? (active) => onTypingChange?.(active, attr.slug)
                : undefined}
            />
          );
          return (
            <div key={attr.slug} className="pp__section" data-required-empty={requiredError ? attr.slug : undefined}>
              <HeadingSmall
                title={isRequired ? `${attr.name} *` : attr.name}
                showSelection={false}
                infoTooltip={infoTooltip}
                onInfoClick={onInfoClick}
                price={priceTag || undefined}
              />
              {useFocusTrigger ? (
                <div
                  id={`mf-trigger-${attr.slug}`}
                  className="mf__trigger"
                  onClick={(e) => { e.preventDefault(); onAttributeFocus?.(attr.slug); openFocusMode(attr.slug); }}
                >
                  {input}
                </div>
              ) : input}
            </div>
          );
        }

        if (attr.type === 'dropdown') {
          const selectedVal = attr.values?.find((v) => v.id === selectedId);
          const priceTag = formatAdditionalPrice(selectedVal?.additional_price);
          // Revealed-by-rule detail dropdown (letter, country) left unanswered — a linked
          // one without its group's option included — errors like a required field once
          // add-to-cart was attempted.
          const revealedError = (showRequiredErrors && requiredEmptySet.has(attr.slug) && selectedId == null)
            ? requiredErrorText : '';
          const useThumbs = !!attr.option_thumbnails;
          const thumb = (url) => url
            ? <img src={url} alt="" className="pp__dropdown-thumb" />
            : undefined;
          const selectedIcon = useThumbs ? thumb(selectedVal?.image_url) : undefined;
          const options = (attr.values || []).map((v) => ({
            value: String(v.id),
            label: v.value,
            icon: useThumbs ? thumb(v.image_url) : undefined,
          }));
          return (
            <div
              key={attr.slug}
              className="pp__section"
              data-required-empty={revealedError ? attr.slug : undefined}
            >
              <HeadingSmall
                title={attr.name}
                showSelection={false}
                infoTooltip={infoTooltip}
                onInfoClick={onInfoClick}
                price={priceTag || undefined}
              />
              <Input
                type="searchable"
                mobileFocus
                mobileFocusReveal={PRODUCT_FOCUS_REVEAL}
                label={attr.name}
                placeholder={attr.name}
                showIcon={!!selectedIcon}
                icon={selectedIcon}
                showOptionIcons={useThumbs}
                options={options}
                value={selectedVal?.value || ''}
                helperText={attr.subtitle || ''}
                error={revealedError || undefined}
                onSelect={(opt) => userChange(attr.slug, opt ? Number(opt.value) : null)}
                onFocus={() => onAttributeFocus?.(attr.slug)}
              />
            </div>
          );
        }

        /* ── select / text → "Lille Brysttryk" layout ── */
        const selectedVal = attr.values.find((v) => v.id === selectedId);
        const headingPrice = formatAdditionalPrice(selectedVal?.additional_price);
        const isSelect = attr.type === 'select';
        // Select-only: show the chosen value next to the title (default on).
        const showValueNextToTitle = isSelect && attr.show_value_next_to_title !== false;
        const selectRevealedError = (showRequiredErrors && requiredEmptySet.has(attr.slug) && selectedId == null)
          ? requiredErrorText : '';

        return (
          <div key={attr.slug} className="pp__section" data-required-empty={selectRevealedError ? attr.slug : undefined}>
            <HeadingSmall
              title={attr.name}
              selection={showValueNextToTitle ? selectedVal?.value : undefined}
              showSelection={showValueNextToTitle}
              extraInfo={attr.subtitle || undefined}
              infoTooltip={infoTooltip}
              onInfoClick={onInfoClick}
              price={headingPrice || undefined}
            />
            <div className="pp__toggle-row">
              {attr.values.map((val) => {
                const isPinnedVal = pinnedIds.includes(val.id);
                const isDisabled  = disabledIds instanceof Set && disabledIds.has(val.id);
                const valuePrice  = formatAdditionalPrice(val.additional_price);
                const iconName    = isSelect ? (val.icon || '').trim() : '';
                // Text labels use the auto-width selector so longer values
                // aren't clipped by the fixed square size; icon options keep it.
                const Selector = iconName ? LabelSelector : LargeLabelSelector;
                return (
                  <div key={val.id} style={isDisabled ? disabledStyle : undefined}>
                    <Selector
                      selected={selectedId === val.id}
                      onClick={(isPinnedVal || isDisabled)
                        ? undefined
                        // Optional attributes are unselectable: clicking the
                        // selected option again clears the choice entirely.
                        : () => userChange(
                            attr.slug,
                            (!attr.is_required && selectedId === val.id) ? null : val.id,
                          )}
                    >
                      {iconName
                        ? <DynamicIcon name={iconName} size={20} fallback={() => <span>{val.value}</span>} />
                        : (valuePrice ? `${val.value} (${valuePrice})` : val.value)}
                    </Selector>
                  </div>
                );
              })}
            </div>
            {selectRevealedError && (
              <div className="typo-body-sm" style={{ color: 'var(--color-ui-error)', marginTop: '6px' }}>
                {selectRevealedError}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default DynamicAttributeControls;
